import { useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import { InfoButton } from '~/components/primitives/InfoButton'
import {
  DELETION_CATEGORIES,
  type DeletionCategory,
  type DeletionPreview,
} from '~/services/selectiveDeletionService'
import styles from './DeletionSection.module.css'

/**
 * Delete one kind of record (Checkpoint 4.3).
 *
 * Follows the same arm-then-confirm shape as the full reset, and for the same
 * reason: **the confirmation states what will be lost rather than asking "are
 * you sure?"**, because there is no undo (D-064). The difference from a reset is
 * that the scope is narrow and said out loud — what goes, what goes with it, and
 * what stops working afterwards.
 *
 * The component reads nothing and writes nothing. Counts arrive as a preview and
 * the deletion is the page's job, which keeps this testable and keeps the one
 * destructive call in one place.
 */

interface DeletionSectionProps {
  /** Live counts, or `undefined` before the section has been opened. */
  preview: DeletionPreview | undefined
  onOpen: () => void
  onDelete: (category: DeletionCategory) => void
  busy: DeletionCategory | undefined
  problem: string | undefined
  /** Set after a successful delete, so the outcome is stated rather than implied. */
  removed: { category: DeletionCategory; count: number } | undefined
}

export function DeletionSection({
  preview,
  onOpen,
  onDelete,
  busy,
  problem,
  removed,
}: DeletionSectionProps) {
  const [armed, setArmed] = useState<DeletionCategory | undefined>(undefined)

  if (preview === undefined) {
    return (
      <section aria-labelledby="deletion-heading">
        <h2 id="deletion-heading" class={styles.sectionHeading}>
          Delete some of your data
        </h2>
        {/* "There is no undo" stays visible — it is the whole reason this
            control is separate from the full reset (D-104 §3, D-067). What
            moved is the list of what can be deleted, which the next screen
            enumerates anyway. */}
        <div class={styles.noteRow}>
          <p class={styles.note}>Remove one kind of record and keep the rest. There is no undo.</p>
          <InfoButton topic="deleting some data" title="Deleting some of your data">
            <p>
              Weigh-ins, measurements, photos, reviews or recorded sets, one kind at a time. Make a
              backup first if you might want the data back.
            </p>
            <p>
              Your profile, project, phase and goals are not offered here. Removing one would orphan
              the records that point at it, so they belong to the full reset instead.
            </p>
          </InfoButton>
        </div>
        <Button variant="secondary" onClick={onOpen} fullWidth>
          Show what I can delete
        </Button>
      </section>
    )
  }

  return (
    <section aria-labelledby="deletion-heading">
      <h2 id="deletion-heading" class={styles.sectionHeading}>
        Delete some of your data
      </h2>

      {removed === undefined ? null : (
        <p class={styles.note} role="status">
          {removed.count === 0
            ? 'There was nothing left to delete.'
            : `Deleted ${removed.count} ${removed.count === 1 ? 'record' : 'records'}.`}
        </p>
      )}

      {problem === undefined ? null : (
        <p class={styles.problem} role="alert">
          Nothing was deleted. {problem}
        </p>
      )}

      <ul class={styles.list}>
        {DELETION_CATEGORIES.map((entry) => {
          const count = preview.counts[entry.category]
          const isArmed = armed === entry.category

          return (
            <li key={entry.category} class={styles.item}>
              <p class={styles.itemHeading}>
                {entry.label} — {count === 0 ? 'none stored' : `${count} stored`}
                {entry.category === 'photos' && count > 0
                  ? `, about ${Math.round(preview.photoBytes / (1024 * 1024))} MB`
                  : ''}
              </p>

              {isArmed ? (
                <div class={styles.confirmation}>
                  <p class={styles.note}>
                    This deletes {count} {count === 1 ? 'record' : 'records'}
                    {entry.alsoRemoves === undefined ? '' : `, and ${entry.alsoRemoves}`}.{' '}
                    {entry.consequence}
                  </p>
                  <p class={styles.note}>This cannot be undone.</p>
                  <div class={styles.actions}>
                    <Button variant="secondary" onClick={() => setArmed(undefined)}>
                      Keep it
                    </Button>
                    <Button
                      variant="primary"
                      busy={busy === entry.category}
                      onClick={() => {
                        onDelete(entry.category)
                        setArmed(undefined)
                      }}
                    >
                      {/* Carries the count, so the confirm button's accessible
                          name differs from the arm button's. Checkpoint 3 shipped
                          two delete buttons with one name and a screen-reader
                          user heard the question and the answer as the same
                          sentence; a browser test could not address the second
                          one either, which is how it was found. */}
                      Delete {count} {count === 1 ? 'record' : 'records'}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  disabled={count === 0}
                  onClick={() => setArmed(entry.category)}
                >
                  Delete {entry.label.toLowerCase()}
                </Button>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
